/**
 * OCR Module for Card Scanner
 *
 * Uses Tesseract.js for text recognition from card images.
 * Optimized for reading Pokemon card names and set numbers.
 */

// Tesseract.js will be loaded dynamically
let worker: any = null;

export interface OCRResult {
  text: string;
  confidence: number;
  words: Array<{
    text: string;
    confidence: number;
    bbox: { x0: number; y0: number; x1: number; y1: number };
  }>;
}

/**
 * Initialize Tesseract worker
 */
async function initWorker(): Promise<void> {
  if (worker) return;

  // Dynamic import to avoid loading Tesseract until needed
  const Tesseract = await import('tesseract.js');

  worker = await Tesseract.createWorker('eng', 1, {
    logger: (m: any) => {
      if (m.status === 'recognizing text') {
        console.log(`OCR Progress: ${Math.round(m.progress * 100)}%`);
      }
    }
  });

  // Optimize for Pokemon card text
  await worker.setParameters({
    tessedit_char_whitelist: 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-/. éèêëàâäùûüôöîï',
    tessedit_pageseg_mode: '7', // Single line mode
    preserve_interword_spaces: '1',
  });

  tesseractLoaded = true;
}

/**
 * Recognize text from canvas
 */
export async function recognizeText(canvas: HTMLCanvasElement): Promise<OCRResult> {
  await initWorker();

  const { data } = await worker.recognize(canvas);

  return {
    text: data.text.trim(),
    confidence: data.confidence,
    words: data.words.map((w: any) => ({
      text: w.text,
      confidence: w.confidence,
      bbox: w.bbox,
    })),
  };
}

/**
 * Recognize card name from the name region
 */
export async function recognizeCardName(nameCanvas: HTMLCanvasElement): Promise<{
  name: string;
  confidence: number;
}> {
  const result = await recognizeText(nameCanvas);

  // Clean up common OCR errors in Pokemon names
  let name = result.text
    .replace(/[|[\]{}]/g, '') // Remove brackets
    .replace(/\d+\s*HP$/i, '') // Remove HP at end
    .replace(/^\s*ex\s*/i, '') // Remove stray "ex" at start
    .replace(/\s+/g, ' ') // Normalize spaces
    .trim();

  // Fix common Pokemon name OCR errors
  const corrections: Record<string, string> = {
    'Pikachu': 'Pikachu',
    'Charizard': 'Charizard',
    'Bulbasaur': 'Bulbasaur',
    'Mew': 'Mew',
    'Mewtwo': 'Mewtwo',
    // Add more as needed
  };

  // Check for similar matches
  for (const correct of Object.keys(corrections)) {
    if (name.toLowerCase().includes(correct.toLowerCase())) {
      name = name.replace(new RegExp(correct, 'i'), correct);
    }
  }

  return {
    name,
    confidence: result.confidence,
  };
}

/**
 * Recognize set number from the set number region
 */
export async function recognizeSetNumber(setCanvas: HTMLCanvasElement): Promise<{
  setNumber: string;
  setCode?: string;
  cardNumber?: string;
  confidence: number;
}> {
  const result = await recognizeText(setCanvas);

  // Parse set number format: "123/456" or "SV3PT5-123" etc.
  const text = result.text.trim();

  // Try to match "XXX/YYY" format
  const fractionMatch = text.match(/(\d+)\s*\/\s*(\d+)/);
  if (fractionMatch) {
    return {
      setNumber: `${fractionMatch[1]}/${fractionMatch[2]}`,
      cardNumber: fractionMatch[1],
      confidence: result.confidence,
    };
  }

  // Try to match set code format like "SV3PT5-123"
  const codeMatch = text.match(/([A-Z0-9]+)-(\d+)/i);
  if (codeMatch) {
    return {
      setNumber: `${codeMatch[1]}-${codeMatch[2]}`,
      setCode: codeMatch[1],
      cardNumber: codeMatch[2],
      confidence: result.confidence,
    };
  }

  return {
    setNumber: text,
    confidence: result.confidence,
  };
}

/**
 * Full card OCR - extracts both name and set number
 */
export async function recognizeCard(
  nameCanvas: HTMLCanvasElement,
  setCanvas: HTMLCanvasElement
): Promise<{
  name: string;
  setNumber: string;
  setCode?: string;
  cardNumber?: string;
  confidence: number;
}> {
  const [nameResult, setResult] = await Promise.all([
    recognizeCardName(nameCanvas),
    recognizeSetNumber(setCanvas),
  ]);

  return {
    name: nameResult.name,
    setNumber: setResult.setNumber,
    setCode: setResult.setCode,
    cardNumber: setResult.cardNumber,
    confidence: Math.min(nameResult.confidence, setResult.confidence),
  };
}

/**
 * Check if OCR confidence is high enough for matching
 */
export function isConfidenceAcceptable(confidence: number): boolean {
  return confidence >= 70;
}

/**
 * Terminate worker to free memory
 */
export async function terminateOCR(): Promise<void> {
  if (worker) {
    await worker.terminate();
    worker = null;
    tesseractLoaded = false;
  }
}
